<template>
  <div
    class="h-full z-50 sm:w-96 w-full pb-safe pt-safe sm:absolute fixed right-0 top-0 bg-n-800 border-l border-n-700"
  >
    <div class="h-full pt-8 px-4 pb-4">
      <div class="absolute top-2 right-2">
        <l-button icon="close" size="small" type="text" @click="$emit('close')" />
      </div>

      <div class="flex flex-col gap-4 h-full">
        <l-form
          ref="formRef"
          class="flex flex-col gap-4 overflow-y-auto flex-1"
          @submit="values => save(values)"
          :validation-schema="formSchema"
          v-slot="{ values }"
        >
          <l-form-field name="name" class="w-full" v-slot="{ field }">
            <l-form-label :label="t('printers.name')" />
            <l-input v-bind="field" :placeholder="t('printers.name')" />
            <l-form-error />
          </l-form-field>

          <l-form-field
            name="type"
            class="w-full"
            :initial-value="PRINTER_TYPES[0].value"
            v-slot="{ field }"
          >
            <l-form-label :label="t('printers.type')" />
            <l-select v-bind="field" :options="PRINTER_TYPES" />
            <l-form-error />
          </l-form-field>

          <l-form-field
            v-if="
              values.type &&
              ['EscPos', 'EscPosMatrix', 'StarPRNT', 'StarGraphic'].includes(values.type)
            "
            name="ip"
            class="w-full"
            v-slot="{ field }"
          >
            <l-form-label :label="t('printers.ip')" />
            <l-input v-bind="field" :placeholder="t('printers.ip')" />
            <l-form-error />
          </l-form-field>
        </l-form>

        <div class="shrink-0">
          <l-button
            class="w-full"
            size="small"
            type="primary"
            :loading="loading"
            @click="formRef?.requestSubmit()"
          >
            {{ t('ctas.add') }}
          </l-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import { onMounted, ref, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { z } from 'zod'

import { isIp } from '@last/core'
import {
  LButton,
  LForm,
  LFormError,
  LFormField,
  LFormLabel,
  LInput,
  LSelect
} from '@last/core-ui/paprika'

import api from '@/api/fetch'
import { useNotifications } from '@/composables/useNotifications'
import type { DiscoveredPrinter, PrinterProtocol } from '@/integrations/printer/types'

const { t } = useI18n()
const notification = useNotifications()

const props = defineProps<{
  printer?: DiscoveredPrinter
}>()

const emit = defineEmits<{
  close: []
}>()

onMounted(() => {
  if (props.printer) {
    formRef?.value?.setFieldValue('type', props.printer.type || 'EscPos')
    formRef?.value?.setFieldValue('name', props.printer.name)
    formRef?.value?.setFieldValue('ip', props.printer.ip)
  }
})

const formRef = useTemplateRef('formRef')
const schema = z.discriminatedUnion('type', [
  z.object({
    type: z.enum(['ZPL', 'Usb', 'KDS', 'UsbAndroid']),
    name: z.string().trim().nonempty()
  }),
  z.object({
    type: z.enum(['EscPos', 'EscPosMatrix', 'StarPRNT', 'StarGraphic']),
    name: z.string().trim().nonempty(),
    ip: z
      .string()
      .trim()
      .nonempty()
      .refine(value => isIp(value), {
        message: t('printers.ip-invalid')
      })
  })
])
const formSchema = toTypedSchema(schema)

const loading = ref(false)

const PRINTER_TYPES: Array<{ label: string; value: PrinterProtocol }> = [
  { label: 'EscPos', value: 'EscPos' },
  { label: 'EscPosMatrix', value: 'EscPosMatrix' },
  { label: 'StarPRNT', value: 'StarPRNT' },
  { label: 'StarGraphic', value: 'StarGraphic' },
  { label: 'ZPL', value: 'ZPL' },
  { label: 'USB', value: 'Usb' },
  { label: 'Android USB', value: 'UsbAndroid' },
  { label: 'KDS', value: 'KDS' }
]

async function save(values: z.infer<typeof schema>) {
  loading.value = true
  api
    .post('/printers', { printers: [values] })
    .then(() => {
      notification.notifySuccess({
        title: t('printers.added')
      })
      emit('close')
    })
    .finally(() => {
      loading.value = false
    })
}
</script>
